import Button from '@mui/material/Button';
import { useAuth, usePersonalSettings } from '@tyro/api';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { useEffect, useId, useRef, useState } from 'react';
import { useIdleTracker } from '../hooks/use-idle-tracker';

export function IdleModal() {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  const { t } = useTranslation(['common']);

  const { security_logout_inactiveTimeUntilLogoutMinutes: inactiveTime } =
    usePersonalSettings();

  const { isIdle, isPastIdle, resetActivity } = useIdleTracker(
    inactiveTime ?? 30,
  );

  const { logout } = useAuth();
  const timerRef = useRef<NodeJS.Timeout>(undefined);
  const [countDown, setCountDown] = useState(60);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
      setCountDown(60);
    }
  };

  const onClose = () => {
    resetActivity();
    clearTimer();
  };

  useEffect(() => {
    if (isPastIdle) {
      logout();
    } else if (isIdle) {
      timerRef.current = setInterval(() => {
        setCountDown((prev) => {
          if (prev <= 0) {
            onClose();
            logout();
            return 0;
          }

          return prev - 1;
        });
      }, 1000);
      return () => clearTimer();
    }
  }, [isIdle, isPastIdle]);

  return (
    <Dialog
      open={isIdle}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <DialogTitle id={titleId} onClose={onClose}>
        {t('common:helloAnyoneHome')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id={descriptionId}>
          {t('common:itLooksLikeYouveBeenIdle', { count: countDown })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={logout}>
          {t('common:actions.logMeOut')}
        </Button>
        <Button variant="soft" onClick={onClose}>
          {t('common:actions.stayLoggedIn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
